import { computed } from '@angular/core';

import { getState, patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Company } from '@prisma/client';

import { Language } from '@hmax-order/shared/enums';

/**
 * Sessão do usuário logado.
 *
 * ⚠️ **Todo campo que a API devolve precisa estar declarado aqui.** O `patchState`
 * do SignalStore **descarta em silêncio** propriedade que não existe no estado
 * inicial — diferente do NGXS, que espalhava o payload inteiro e guardava campos
 * não declarados. Campo faltando aqui vira dado perdido no login, sem erro.
 *
 * A projeção do backend está em `user.service.ts` (`$project.user`) e o tipo
 * compartilhado em `@hmax-order/shared/types`. Ao mexer em qualquer um dos dois,
 * espelhe aqui e em `defaults`.
 *
 * ⚠️ **Campo ausente é `| undefined`, nunca `?`.** O SignalStore preserva a opcionalidade
 * NO MEMBRO: com `avatar?: string` o `store.avatar` é `Signal | undefined` e toda leitura
 * exige `?.()` sob o `strict: true` das libs. Com a chave obrigatória e o valor opcional,
 * o membro é `Signal<string | undefined>` — mesmo dado, sem o `?.()`. Quem manda pedaço do
 * usuário usa `UserStatePayload`.
 */
export interface UserStateModel {
  id: string;
  name: string;
  email: string;
  avatar: string | undefined;
  status: string | undefined;
  roles: string[] | undefined;
  companies: { id?: string; name?: string }[] | undefined;
  currentCompanyId: string | undefined;
  currentOrganizationId: string | undefined;
  active: boolean | undefined;
  personId: string | undefined;
  language: Language | undefined;
  darkMode: boolean | undefined;
  initializeRoute: string | undefined;
  initialRoute: string | undefined;
  hasLocalPassword: boolean | undefined;
  accessScheduleEnabled: boolean | undefined;
  accessStartTime: string | undefined;
  accessEndTime: string | undefined;
  accessWeekdays: number[] | undefined;
  /** Enriquecidos por `getUserForLogin`, fora do `$project` da pipeline. */
  locale: string | undefined;
  timezone: string | undefined;
  demo: boolean | undefined;
}

/**
 * O que uma chamada de `setUser` pode trazer: a resposta do login manda o usuário inteiro,
 * o PATCH `/me` manda um pedaço. `patchState` descarta chave desconhecida e ignora chave
 * ausente, então parcial é o contrato real — e é o que os call sites já passam.
 */
export type UserStatePayload = Partial<UserStateModel>;

export const USER_DEFAULTS: UserStateModel = {
  id: '',
  name: '',
  email: '',
  avatar: '',
  status: '',
  roles: [],
  companies: [],
  currentCompanyId: '',
  currentOrganizationId: '',
  personId: '',
  language: Language.portuguese,
  active: false,
  darkMode: false,
  initializeRoute: '',
  initialRoute: '',
  hasLocalPassword: undefined,
  accessScheduleEnabled: undefined,
  accessStartTime: undefined,
  accessEndTime: undefined,
  accessWeekdays: undefined,
  locale: undefined,
  timezone: undefined,
  demo: undefined,
};

/**
 * Estado de sessão do usuário logado.
 *
 * Diferente de cfg/product/company-header, `user` **não** é dado de servidor lido via
 * `apiResource`: é populado imperativamente pela resposta do login
 * (`AuthService.setToken`) e atualizado por PATCH `/me`. Por isso é store, não recurso.
 *
 * A navegação com reload duro que o `@Action(SetUser)` do NGXS fazia ao trocar de
 * empresa **não mora aqui** — mutar estado não deveria disparar navegação escondida.
 * Quem troca de empresa (`CompanySelectComponent`) decide e executa esse reload.
 */
export const UserStore = signalStore(
  { providedIn: 'root' },
  withState<UserStateModel>(USER_DEFAULTS),
  withComputed((store) => ({
    // Lista manual esquece campo novo em silêncio — o mesmo defeito de `patchState`.
    // A garantia é o teste "getUser expõe todos os campos do estado" em user.store.spec.
    getUser: computed<UserStateModel>(() => ({
      id: store.id(),
      name: store.name(),
      email: store.email(),
      avatar: store.avatar(),
      status: store.status(),
      roles: store.roles(),
      companies: store.companies(),
      currentCompanyId: store.currentCompanyId(),
      currentOrganizationId: store.currentOrganizationId(),
      active: store.active(),
      personId: store.personId(),
      language: store.language(),
      darkMode: store.darkMode(),
      initializeRoute: store.initializeRoute(),
      initialRoute: store.initialRoute(),
      hasLocalPassword: store.hasLocalPassword(),
      accessScheduleEnabled: store.accessScheduleEnabled(),
      accessStartTime: store.accessStartTime(),
      accessEndTime: store.accessEndTime(),
      accessWeekdays: store.accessWeekdays(),
      locale: store.locale(),
      timezone: store.timezone(),
      demo: store.demo(),
    })),
    getName: computed(() => store.name()),
    getRoles: computed(() => store.roles()),
    getCompanies: computed<Partial<Company>[] | undefined>(() => store.companies()),
    getCurrentCompany: computed<Partial<Company> | undefined>(() => {
      const currentCompanyId = store.currentCompanyId();
      const companies = store.companies();

      if (currentCompanyId && companies) {
        return companies.find((company) => company.id === currentCompanyId) ?? undefined;
      }

      return undefined;
    }),
    getInitialRoute: computed(() => store.initialRoute()),
    // Ponto único de leitura do id da empresa: recurso por empresa depende deste computed
    // (isolamento entre tenants), então a derivação mora aqui e não em cada service.
    getCurrentCompanyId: computed<string | undefined>(() => store.currentCompanyId() || undefined),
  })),
  withMethods((store) => ({
    /**
     * Substitui o usuário da sessão e persiste em `localStorage`.
     *
     * `payload` nulo (logout) limpa o `localStorage` e volta aos defaults — sem
     * navegação: quem chama decide o que fazer com a troca de empresa.
     */
    setUser(payload: UserStatePayload | null): void {
      if (!payload) {
        localStorage.removeItem('user');
        patchState(store, USER_DEFAULTS);
        return;
      }

      patchState(store, payload);
      // Persiste o ESTADO, não o payload: `patchState` filtra chave desconhecida e o
      // `setItem` não filtraria nada. Gravar o payload cru faz store e localStorage
      // divergirem sempre que a resposta vier com shape inesperado — e os guards leem
      // o localStorage, não o store.
      localStorage.setItem('user', JSON.stringify(getState(store)));
    },

    /** Reseta para os defaults e limpa o `localStorage`, sem navegação. */
    clear(): void {
      localStorage.removeItem('user');
      patchState(store, USER_DEFAULTS);
    },
  })),
);
